import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../app/Icon";
import { importAiSource } from "../ai/aiClient";
import { AI_STYLES, exampleResult, type AiSource, type AiStyle } from "../ai/aiTypes";
import { pendingStencilDestination, savePendingStencilIntake } from "../ai/pendingStencilIntake";
import { useAnalytics } from "../analytics/analytics";
import { useAuth } from "../auth/auth";

const STYLE_DESCRIPTIONS: Readonly<Record<AiStyle, string>> = {
  outline: "Essential lines.",
  standard: "Finer detail.",
  hatching: "Shading guides.",
};

function revokeSource(source: AiSource | null) {
  if (!source) return;
  URL.revokeObjectURL(source.url);
  if (source.original?.url && source.original.url !== source.url) URL.revokeObjectURL(source.original.url);
}

export function LandingIntake() {
  const { account, loading: authLoading } = useAuth();
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const controller = useRef<AbortController | null>(null);
  const sourceRef = useRef<AiSource | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState<AiSource | null>(null);
  const [style, setStyle] = useState<AiStyle>("standard");
  const [opening, setOpening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { sourceRef.current = source; }, [source]);
  useEffect(() => () => {
    controller.current?.abort();
    revokeSource(sourceRef.current);
  }, []);

  async function openFile(nextFile: File, method: "picker" | "drop" | "paste") {
    if (opening || saving) return;
    controller.current?.abort();
    const nextController = new AbortController();
    controller.current = nextController;
    setOpening(true); setError("");
    try {
      const nextSource = await importAiSource(nextFile, nextController.signal);
      if (nextController.signal.aborted) { revokeSource(nextSource); return; }
      revokeSource(sourceRef.current);
      sourceRef.current = nextSource;
      setSource(nextSource); setFile(nextFile);
      analytics.track("reference_added", { experiment: "lp-2", method, media_type: nextSource.mediaType ?? "unknown" });
    } catch (cause) {
      if (!nextController.signal.aborted) setError(cause instanceof Error ? cause.message : "This file could not be opened.");
    } finally {
      if (controller.current === nextController) { controller.current = null; setOpening(false); }
    }
  }

  function drop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault(); setDragging(false);
    if (event.dataTransfer.files.length !== 1) { setError("Add one image at a time."); return; }
    const nextFile = event.dataTransfer.files[0];
    if (nextFile) void openFile(nextFile, "drop");
  }

  function paste(event: ClipboardEvent<HTMLElement>) {
    if (event.target instanceof HTMLInputElement || opening || saving) return;
    const files = Array.from(event.clipboardData.files);
    if (files.length !== 1 || !files[0]) return;
    event.preventDefault();
    void openFile(files[0], "paste");
  }

  async function continueToAccount() {
    if (!file || !source || opening || saving) return;
    setSaving(true); setError("");
    try {
      const id = await savePendingStencilIntake(file, style);
      try { sessionStorage.setItem("stencil-ai-style", style); } catch { /* The stored handoff remains authoritative. */ }
      const destination = pendingStencilDestination(id);
      analytics.track("setup_completed", { experiment: "lp-2", style, account_state: account ? "signed_in" : "anonymous" });
      if (account) void navigate(destination);
      else void navigate(`/sign-up?${new URLSearchParams({ from: destination })}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Your image could not be saved before sign-up. Try again.");
      setSaving(false);
    }
  }

  return <section className="landing-intake ai-studio studio-theme" data-theme="dark" aria-labelledby="landing-intake-title" onPaste={paste}>
    <div className="landing-intake__heading">
      <h2 id="landing-intake-title">Upload and choose a style</h2>
    </div>
    <input ref={fileInput} hidden type="file" accept="image/png,image/jpeg" aria-label="Upload reference image" disabled={opening || saving} onChange={event => {
      const nextFile = event.target.files?.[0];
      if (nextFile) void openFile(nextFile, "picker");
      event.currentTarget.value = "";
    }} />
    <section className="ai-reference" aria-label="Reference image upload">
      <div className="ai-field-heading landing-intake__file-hint"><span className="ai-field-hint">PNG or JPG · up to 10 MB</span></div>
      <button type="button" disabled={opening || saving} className={`ai-dropzone ${source ? "ai-dropzone--loaded" : ""} ${dragging ? "ai-dropzone--dragging" : ""}`}
        aria-label={source ? `Replace ${source.name} reference` : "Choose a reference image"} onClick={() => fileInput.current?.click()} onDrop={drop}
        onDragOver={event => { event.preventDefault(); if (!opening && !saving) setDragging(true); }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }}>
        {source ? <>
          <img className="ai-upload-preview" src={source.url} alt={`${source.name} reference preview`} />
          <span className="ai-upload-details"><span className="ai-upload-ready"><Icon name="check" />Reference added</span><strong>{source.name}</strong>
            <span>{source.width} × {source.height} px</span><span className="ai-dropzone__action"><Icon name="replace" />Replace image</span></span>
        </> : <>
          <span className="ai-upload-art" aria-hidden="true"><span className="ai-upload-art__back"><Icon name="image" /></span><span className="ai-upload-art__front"><Icon name="image" /></span><span className="ai-upload-art__plus"><Icon name="plus" /></span></span>
          <strong>{opening ? "Opening your image…" : dragging ? "Drop it here" : "Drop your artwork here"}</strong>
          <span className="ai-dropzone__subtitle">Drag and drop, or browse your files</span><span className="ai-dropzone__action"><Icon name="arrow-up" />Choose image</span>
        </>}
      </button>
      {source && <div className="ai-reference__caption"><button type="button" className="ai-text-button" disabled={opening || saving} onClick={() => { revokeSource(sourceRef.current); sourceRef.current = null; setSource(null); setFile(null); setError(""); }}>Remove image</button></div>}
    </section>
    <fieldset className="ai-fieldset ai-style-fieldset" disabled={opening || saving}>
      <legend>Choose your stencil style</legend>
      <div className="ai-styles">{AI_STYLES.map(item => <label key={item.id} className={`ai-style ${style === item.id ? "ai-style--selected" : ""}`}>
        <input type="radio" name="landing-stencil-style" value={item.id} aria-label={item.label} aria-describedby={`landing-style-description-${item.id}`} checked={style === item.id} onChange={() => setStyle(item.id)} />
        <span className="ai-style__image"><img src={exampleResult(item.id).url} alt="" /></span>
        <span className="ai-style__copy"><span className="ai-style__title"><strong>{item.label}</strong><span className="ai-selection-mark" aria-hidden="true">{style === item.id && <Icon name="check" />}</span></span>
          <small id={`landing-style-description-${item.id}`}>{STYLE_DESCRIPTIONS[item.id]}</small></span>
      </label>)}</div>
    </fieldset>
    {error && <p role="alert" className="ai-message">{error}</p>}
    <div className="ai-intake__actions"><button type="button" className="ai-button ai-button--primary ai-continue" disabled={!source || opening || saving || authLoading} onClick={() => void continueToAccount()}>
      {saving ? "Saving image…" : account ? "Continue to stencil" : "Continue"}<Icon name="arrow-right" />
    </button></div>
  </section>;
}
